import { FC, ReactNode } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import {
  ArrowRightIcon,
  EnvelopeSimpleIcon,
  FileTextIcon,
  type Icon,
  StackIcon,
} from '@phosphor-icons/react';

import type { ProfileSummaryDto } from '../../api/types';
import { ORG_TOKENS } from '../utils/tokens';

interface Props {
  summary: ProfileSummaryDto | undefined;
  completedCount: number;
  categoryCount: number;
  docApprovedCount: number;
  docProcessingCount: number;
  onViewActivity?: () => void;
  onReviewAll?: () => void;
}

const Card: FC<{ children: ReactNode; tinted?: boolean }> = observer(({
  children,
  tinted,
}) => (
  <Box
    sx={{
      p: { xs: 1.5, sm: 2 },
      borderRadius: `${ORG_TOKENS.radiusMd}px`,
      backgroundColor: tinted ? 'yellow.100' : 'background.paper',
      border: 1,
      borderColor: (theme) =>
        tinted ? theme.palette.yellow[300] : theme.palette.grey[300],
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
    }}
  >
    {children}
  </Box>
));

const Label: FC<{ children: ReactNode; icon?: Icon; tinted?: boolean }> = observer(({
  children,
  icon: IconCmp,
  tinted,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    {IconCmp && (
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: 1,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: tinted ? 'yellow.800' : 'text.tertiary',
          backgroundColor: tinted ? 'yellow.200' : 'grey.200',
        }}
      >
        <IconCmp size={13} weight="bold" />
      </Box>
    )}
    <Typography variant="textXS_semiBold" color="text.tertiary">
      {children}
    </Typography>
  </Box>
));

const Stat: FC<{ children: ReactNode; color?: string }> = observer(({ children, color }) => (
  <Typography
    variant="text2XL_bold"
    color={color ?? 'text.primary'}
    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
  >
    {children}
  </Typography>
));

const LinkText: FC<{ children: ReactNode; onClick?: () => void; color?: string }> = observer(({
  children,
  onClick,
  color,
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.4,
      border: 0,
      background: 'transparent',
      p: 0,
      cursor: 'pointer',
      fontFamily: 'inherit',
      typography: 'textXS_semiBold',
      color: color ?? 'blue.700',
    }}
  >
    {children}
    <ArrowRightIcon size={12} />
  </Box>
));

const Sub: FC<{ children: ReactNode }> = observer(({ children }) => (
  <Typography variant="textXS_regular" color="text.tertiary">{children}</Typography>
));

/** Top dashboard cards: completion %, documents, fields filled, reviews pending. */
export const ProfileSummaryRow: FC<Props> = observer(
  ({
    summary,
    completedCount,
    categoryCount,
    docApprovedCount,
    docProcessingCount,
    onViewActivity,
    onReviewAll,
  }) => {
    const { t } = useTrans();

    const progressPct = summary?.progressPct ?? 0;
    const documentsUploaded = summary?.documentsUploaded ?? 0;
    const fieldsFilled = summary?.fieldsFilled ?? 0;
    const totalFields = summary?.totalFields ?? 0;
    const pendingReviewCount = summary?.pendingReviewCount ?? 0;
    const remaining = Math.max(totalFields - fieldsFilled, 0);
    const fieldsPct = totalFields > 0 ? (fieldsFilled / totalFields) * 100 : 0;

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 1.5,
        }}
      >
        <Card>
          <Label>{t('app.orgProfile.summary.completion')}</Label>
          <Stat color="blue.700">{progressPct}%</Stat>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': { backgroundColor: 'blue.700' },
            }}
          />
          <Sub>
            {t('app.orgProfile.summary.categoriesApproved', {
              approved: completedCount,
              total: categoryCount,
            })}
          </Sub>
        </Card>

        <Card>
          <Label icon={FileTextIcon}>{t('app.orgProfile.summary.documents')}</Label>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Stat>{documentsUploaded}</Stat>
            <LinkText onClick={onViewActivity}>
              {t('app.orgProfile.summary.viewActivity')}
            </LinkText>
          </Box>
          <Sub>
            {t('app.orgProfile.summary.docBreakdown', {
              approved: docApprovedCount,
              processing: docProcessingCount,
            })}
          </Sub>
        </Card>

        <Card>
          <Label icon={StackIcon}>{t('app.orgProfile.summary.fieldsFilled')}</Label>
          <Stat>
            {fieldsFilled}
            <Box component="span" sx={{ typography: 'textSM_regular', color: 'text.disabled' }}>
              /{totalFields}
            </Box>
          </Stat>
          <LinearProgress
            variant="determinate"
            value={fieldsPct}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': { backgroundColor: 'text.primary' },
            }}
          />
          <Sub>{t('app.orgProfile.summary.fieldsRemaining', { n: remaining })}</Sub>
        </Card>

        <Card tinted>
          <Label icon={EnvelopeSimpleIcon} tinted>
            {t('app.orgProfile.summary.reviewsPending')}
          </Label>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Stat color="yellow.800">{pendingReviewCount}</Stat>
            {pendingReviewCount > 0 && (
              <LinkText onClick={onReviewAll} color="yellow.800">
                {t('app.orgProfile.summary.reviewAll')}
              </LinkText>
            )}
          </Box>
          <Sub>{t('app.orgProfile.summary.reviewsHint')}</Sub>
        </Card>
      </Box>
    );
  },
);
